import React, { useState, useEffect } from "react";
import { Card, Button } from "flowbite-react";
import { useStore } from "../store";
import Table from "../lib/ui/components/table/Table";
import Checkbox from "../lib/ui/components/checkbox/Checkbox";
import { Heading1, Heading2, SmallText, Heading4 } from "../lib/ui/components/typography/Typography";

const AnalyticsPage = () => {
  const [stats, setStats] = useState(null);
  const [allCompanies, setAllCompanies] = useState([]);
  const [selectedOrgs, setSelectedOrgs] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = useStore((state) => state.token);

  const fetchStats = async (orgIds = null) => {
    if (!token) return;
    setLoading(true);

    try {
      // Use REACT_APP_API_URL which includes /api/v1, fallback also needs it
      const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";
      
      let url = `${baseUrl}/analytics/global-stats`;
      
      if (orgIds && orgIds.length > 0) {
        const params = new URLSearchParams();
        orgIds.forEach(id => params.append('company_ids', id));
        url = `${url}?${params.toString()}`;
      }
      
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 403) {
        setError("Access denied. You do not have permission to view this page.");
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to fetch analytics");
      }

      const data = await response.json();
      setStats(data);
      
      // On initial load, save the full list of companies
      if (!orgIds && allCompanies.length === 0) {
        setAllCompanies(data.by_company);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [token]);

  const handleOrgToggle = (companyId) => {
    const newSelected = new Set(selectedOrgs);
    if (newSelected.has(companyId)) {
      newSelected.delete(companyId);
    } else {
      newSelected.add(companyId);
    }
    setSelectedOrgs(newSelected);
  };

  const handleUpdateStats = () => {
    const orgIds = Array.from(selectedOrgs);
    fetchStats(orgIds.length > 0 ? orgIds : null);
  };

  if (loading && !stats) return <div className="p-10 text-center">Loading analytics...</div>;
  
  if (error) {
    return (
      <div className="p-10 text-center">
        <Heading2 className="text-red-500 mb-4">Error</Heading2>
        <SmallText>{error}</SmallText>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="container mx-auto px-4 py-8">
      <Heading1 className="mb-6">Platform Conversion Analytics</Heading1>
      
      {/* Global Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card>
          <div className="flex flex-col items-center justify-center py-4">
            <Heading2 className="text-blue-600 mb-2">
              {stats.global.conversion_rate}%
            </Heading2>
            <Heading4 className="text-gray-600">Completion Rate</Heading4>
            <SmallText className="text-gray-500 mt-2">
              Global Average
            </SmallText>
          </div>
        </Card>
        
        <Card>
          <div className="flex flex-col items-center justify-center py-4">
            <Heading2 className="mb-2">{stats.global.started}</Heading2>
            <Heading4 className="text-gray-600">Started</Heading4>
            <SmallText className="text-gray-500 mt-2">
              Interviews (Non-Pending)
            </SmallText>
          </div>
        </Card>

        <Card>
          <div className="flex flex-col items-center justify-center py-4">
            <Heading2 className="mb-2">{stats.global.completed}</Heading2>
            <Heading4 className="text-gray-600">Completed</Heading4>
            <SmallText className="text-gray-500 mt-2">
              Finished Interviews
            </SmallText>
          </div>
        </Card>
      </div>

      {/* Company Breakdown */}
      <Card>
        <div className="flex justify-between items-center mb-4">
          <div>
            <Heading2>Performance by Customer</Heading2>
            <SmallText className="text-gray-500">Sorted by volume</SmallText>
          </div>
          <Button onClick={handleUpdateStats} disabled={loading} color="blue">
            {loading ? 'Updating...' : 'Submit'}
          </Button>
        </div>
        <div className="overflow-x-auto">
          <Table hoverable>
            <Table.Head>
              <Table.HeadCell className="p-4">
                <Checkbox 
                  checked={selectedOrgs.size === allCompanies.length && allCompanies.length > 0}
                  onChange={() => {
                    if (selectedOrgs.size === allCompanies.length) {
                      setSelectedOrgs(new Set());
                    } else {
                      setSelectedOrgs(new Set(allCompanies.map(c => c.company_id)));
                    }
                  }}
                />
              </Table.HeadCell>
              <Table.HeadCell>Company Name</Table.HeadCell>
              <Table.HeadCell>Started</Table.HeadCell>
              <Table.HeadCell>Completed</Table.HeadCell>
              <Table.HeadCell>Completion Rate</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {allCompanies.map((company) => (
                <Table.Row key={company.company_id} className="bg-white">
                  <Table.Cell className="p-4">
                    <Checkbox 
                      checked={selectedOrgs.has(company.company_id)}
                      onChange={() => handleOrgToggle(company.company_id)}
                    />
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap font-medium text-gray-900">
                    {company.company_name}
                  </Table.Cell>
                  <Table.Cell>{company.started}</Table.Cell>
                  <Table.Cell>{company.completed}</Table.Cell>
                  <Table.Cell>
                    <div className="flex items-center gap-2">
                      <div className="w-full bg-gray-200 rounded-full h-2.5 max-w-[100px]">
                        <div 
                          className="bg-blue-600 h-2.5 rounded-full" 
                          style={{ width: `${Math.min(company.conversion_rate, 100)}%` }}
                        ></div>
                      </div>
                      <span className="font-medium">{company.conversion_rate}%</span>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
              {allCompanies.length === 0 && (
                <Table.Row className="bg-white">
                  <Table.Cell colSpan={5} className="text-center py-4">
                    No interview data available.
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>
      </Card>
    </div>
  );
};

export default AnalyticsPage;
